import type { Extent } from "ol/extent";

import type { SearchSitesParams } from "../../../api/site/search";
import { searchSites } from "../../../api/site/search";
import type { Site } from "../../../api/site/structs";
import { ObsNetworkId } from "../../../enums";
import { checkIn, checkOut } from "../../../utils/blocker";
import { Tabs } from "../../../utils/bulmautil/tabs";
import hx from "../../../utils/hydroxie";
import { InheritableEventEmitter } from "../../../utils/inheritableeventemitter";

import { BasicPanel } from "./panels/basicpanel";
import { SoilPanel } from "./panels/soilpanel";

/**
 * 観測点検索→検索条件エリア
 */
export class ConditionArea
    extends InheritableEventEmitter<{ update: [results: Site[] | undefined] }> {

    /** 検索パラメーター */
    #searchParams?: Partial<SearchSitesParams>;
    /** タブコントロール */
    #tabs: Tabs;
    /** 基本情報パネル */
    #basicPanel: BasicPanel;
    /** 地盤情報パネル */
    #soilPanel: SoilPanel;
    /** 検索ボタン要素 */
    #searchButton: HTMLElement;

    /**
     * コンストラクター。
     *
     * @param elem 検索条件エリアのルート要素
     */
    constructor(elem: HTMLElement) {
        super();

        const basicPanelElem = hx("div", { id: "condition_basic", className: "condition-panel condition-basic" });
        this.#basicPanel = new BasicPanel(basicPanelElem);

        const soilPanelElem = hx("div", { id: "condition_soil", className: "condition-panel condition-soil hidden" });
        this.#soilPanel = new SoilPanel(soilPanelElem);

        const tabElem = hx("div", { className: "tabs is-fullwidth" },
            hx("ul",
                hx("li", { className: "is-active" }, hx("a", "基本")),
                hx("li", hx("a", "J-SHIS地盤情報"))));
        this.#tabs = new Tabs({ rootElement: tabElem });

        const searchButton = hx("button", { id: "condition_search", className: "button" }, "検索");
        this.#searchButton = searchButton;

        elem.replaceChildren(tabElem, basicPanelElem, soilPanelElem, searchButton);
    }

    /**
     * 初期化処理を行う。
     * タブ切り替えや検索ボタンのイベントリスナーを設定する。
     */
    async init() {
        const panels = [
            this.#basicPanel,
            this.#soilPanel,
        ];

        this.#tabs.on("change", (_, index) => {
            for (const [i, panel] of panels.entries()) {
                if (i === index) {
                    panel.show();
                } else {
                    panel.hide();
                }
            }
        });

        this.#searchButton.addEventListener("click", async () => {
            try {
                checkIn("観測点を検索中です。しばらくお待ち下さい。");

                const results = await searchSites(this.#searchParams);
                this.emit("update", results);
            } catch (e) {
                console.error(e);
                alert((Error.isError(e)) ? e.message : e);
            } finally {
                checkOut();
            }
        });

        for (const panel of panels) {
            panel.on("update", async (params) => {
                this.#searchParams = {
                    ...this.#searchParams,
                    ...params,
                };
            });
        }

        await Promise.all(panels.map(async (x) => x.init()));
    }

    /**
     * 地図上で選択された範囲を検索条件に設定する。
     *
     * @param extent 選択された範囲（Extent）
     */
    setExtent(extent: Extent) {
        this.#basicPanel.setExtent(extent);
    }

    /**
     * 初期データを読み込む。
     * 初期状態でK-NETとKiK-netのデータを検索する。
     */
    loadInitialData() {
        this.#searchParams = {
            obsNetworkId: [
                ObsNetworkId.KNet,
                ObsNetworkId.KiKnet,
            ],
        };

        this.#searchButton.click();
        this.#searchParams = {};
        this.#basicPanel.trigger();
        this.#soilPanel.trigger();
    }
}
